<template>
  <div class="admin-user-history container py-4">
    <!-- Breadcrumbs -->
    <nav aria-label="breadcrumb" class="mb-3">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link to="/admin" class="text-decoration-none">
            <font-awesome-icon icon="home" class="me-1" />
            Admin
          </router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link to="/admin/users" class="text-decoration-none">
            Benutzerverwaltung
          </router-link>
        </li>
        <li class="breadcrumb-item active" aria-current="page">Benutzer-Historie</li>
      </ol>
    </nav>

    <div class="container-fluid p-4" id="main-content">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 class="mb-0">
            <font-awesome-icon icon="history" class="me-2" />
            Benutzer-Historie
          </h1>
          <p class="text-muted mb-0" v-if="user">
            Historie für {{ user.firstName }} {{ user.lastName }} ({{ user.email }})
          </p>
        </div>
        <BackButton />
      </div>

      <!-- Search and Filters -->
      <div class="card shadow-sm mb-4">
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-4">
              <label for="search" class="form-label">Suche</label>
              <div class="input-group">
                <span class="input-group-text">
                  <font-awesome-icon icon="search" />
                </span>
                <input
                  v-model="searchQuery"
                  type="text"
                  class="form-control"
                  id="search"
                  placeholder="Nach Daten oder Referenz-ID suchen..."
                  @input="debouncedSearch"
                />
                <button
                  v-if="searchQuery"
                  type="button"
                  class="btn btn-outline-secondary"
                  @click="clearSearch"
                  title="Suche zurücksetzen"
                >
                  <font-awesome-icon icon="times" />
                </button>
              </div>
            </div>
            <div class="col-md-2">
              <label for="actionFilter" class="form-label">Aktion</label>
              <select v-model="actionFilter" class="form-select" id="actionFilter" @change="loadHistory">
                <option value="">Alle Aktionen</option>
                <option value="create">Erstellt</option>
                <option value="update">Aktualisiert</option>
                <option value="delete">Gelöscht</option>
              </select>
            </div>
            <div class="col-md-3">
              <label for="referenceTypeFilter" class="form-label">Referenz-Typ</label>
              <select v-model="referenceTypeFilter" class="form-select" id="referenceTypeFilter" @change="loadHistory">
                <option value="">Alle Typen</option>
                <option value="user">Benutzer</option>
                <option value="receipt">Quittung</option>
                <option value="team">Team</option>
                <option value="billing">Rechnung</option>
                <option value="coinConsumption">Coin-Verbrauch</option>
                <option value="userAccount">Benutzerkonto</option>
                <option value="email">E-Mail</option>
                <option value="role">Rolle</option>
              </select>
            </div>
            <div class="col-md-3">
              <label for="itemsPerPage" class="form-label">Einträge pro Seite</label>
              <select v-model="itemsPerPage" class="form-select" id="itemsPerPage" @change="loadHistory">
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>
        </div>
      </div>

          <!-- Active Filters -->
          <div v-if="hasActiveFilters" class="card shadow-sm mb-3">
            <div class="card-body py-2">
              <div class="d-flex align-items-center flex-wrap gap-2">
                <span class="text-muted small">Aktive Filter:</span>
                <span v-if="searchQuery" class="badge bg-primary">
                  Suche: "{{ searchQuery }}"
                  <button type="button" class="btn-close btn-close-white ms-1" @click="clearSearch" aria-label="Entfernen"></button>
                </span>
                <span v-if="actionFilter" class="badge bg-info">
                  Aktion: {{ getActionLabel(actionFilter) }}
                  <button type="button" class="btn-close btn-close-white ms-1" @click="actionFilter = ''; loadHistory()" aria-label="Entfernen"></button>
                </span>
                <span v-if="referenceTypeFilter" class="badge bg-secondary">
                  Typ: {{ getReferenceTypeLabel(referenceTypeFilter) }}
                  <button type="button" class="btn-close btn-close-white ms-1" @click="referenceTypeFilter = ''; loadHistory()" aria-label="Entfernen"></button>
                </span>
                <button type="button" class="btn btn-outline-secondary btn-sm" @click="clearAllFilters">
                  Alle Filter zurücksetzen
                </button>
              </div>
            </div>
          </div>

          <!-- History Table -->
          <div class="card shadow-sm">
            <div class="card-body">
          <!-- Loading -->
          <div v-if="loading" class="text-center my-5">
            <div class="spinner-border" role="status">
              <span class="visually-hidden">Historie wird geladen...</span>
            </div>
          </div>

          <!-- Error -->
          <div v-else-if="error" class="alert alert-danger" role="alert">
            <font-awesome-icon icon="exclamation-triangle" class="me-2" />
            {{ error }}
          </div>

          <!-- History Table -->
          <div v-else>
            <div v-if="history.length === 0" class="text-center text-muted py-5">
              <font-awesome-icon icon="history" class="fa-3x mb-3" />
              <p>Keine Historie-Einträge gefunden.</p>
            </div>
            <div v-else>
              <div class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th>Zeitstempel</th>
                      <th>Aktion</th>
                      <th>Referenz-Typ</th>
                      <th>Referenz-ID</th>
                      <th>Änderungen</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="entry in history" :key="entry.id">
                      <td>
                        <small class="text-muted">
                          {{ formatDate(entry.timestamp) }}
                        </small>
                      </td>
                      <td>
                        <span class="badge" :class="getActionBadgeClass(entry.action)">
                          {{ getActionLabel(entry.action) }}
                        </span>
                      </td>
                      <td>
                        <span class="badge bg-secondary">
                          {{ getReferenceTypeLabel(entry.referenceType) }}
                        </span>
                      </td>
                      <td>
                        <code class="small">{{ entry.referenceId }}</code>
                      </td>
                      <td>
                        <button
                          v-if="entry.action === 'update' && (entry.oldData || entry.newData)"
                          type="button"
                          class="btn btn-outline-info btn-sm"
                          @click="showDiffModal(entry)"
                          title="Änderungen anzeigen"
                        >
                          <font-awesome-icon icon="eye" />
                          Änderungen
                        </button>
                        <button
                          v-else-if="entry.action === 'create' && entry.newData"
                          type="button"
                          class="btn btn-outline-success btn-sm"
                          @click="showDataModal(entry, 'new')"
                          title="Neue Daten anzeigen"
                        >
                          <font-awesome-icon icon="plus" />
                          Neue Daten
                        </button>
                        <button
                          v-else-if="entry.action === 'delete' && entry.oldData"
                          type="button"
                          class="btn btn-outline-danger btn-sm"
                          @click="showDataModal(entry, 'old')"
                          title="Gelöschte Daten anzeigen"
                        >
                          <font-awesome-icon icon="trash" />
                          Gelöschte Daten
                        </button>
                        <span v-else class="text-muted">-</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Pagination -->
              <nav v-if="pagination.totalPages > 1" aria-label="Historie-Navigation">
                <ul class="pagination justify-content-center">
                  <li class="page-item" :class="{ disabled: pagination.currentPage === 1 }">
                    <button class="page-link" @click="changePage(pagination.currentPage - 1)" :disabled="pagination.currentPage === 1">
                      <font-awesome-icon icon="chevron-left" />
                    </button>
                  </li>
                  
                  <li v-for="page in visiblePages" :key="page" class="page-item" :class="{ active: page === pagination.currentPage }">
                    <button class="page-link" @click="changePage(page)">{{ page }}</button>
                  </li>
                  
                  <li class="page-item" :class="{ disabled: pagination.currentPage === pagination.totalPages }">
                    <button class="page-link" @click="changePage(pagination.currentPage + 1)" :disabled="pagination.currentPage === pagination.totalPages">
                      <font-awesome-icon icon="chevron-right" />
                    </button>
                  </li>
                </ul>
              </nav>

              <!-- Pagination Info -->
              <div class="text-center text-muted mt-3">
                Zeige {{ paginationInfo }} von {{ pagination.totalItems }} Einträgen
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Diff Modal -->
    <Teleport to="#modalTarget">
      <div v-if="showDiffModalFlag" class="modal-backdrop fade show"></div>
      <div v-if="showDiffModalFlag" class="modal show d-block" tabindex="-1" role="dialog">
        <div class="modal-dialog modal-xl" role="document">
          <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Änderungen anzeigen
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closeDiffModal"
              aria-label="Schließen"
            ></button>
          </div>
          <div class="modal-body">
            <div class="row">
              <div class="col-md-6">
                <h6 class="text-danger">Alte Werte</h6>
                <pre class="bg-light p-3 rounded"><code>{{ formatJson(selectedEntry?.oldData) }}</code></pre>
              </div>
              <div class="col-md-6">
                <h6 class="text-success">Neue Werte</h6>
                <pre class="bg-light p-3 rounded"><code>{{ formatJson(selectedEntry?.newData) }}</code></pre>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeDiffModal">
              Schließen
            </button>
          </div>
        </div>
      </div>
    </div> </Teleport>

    <!-- Data Modal -->
    <Teleport to="#modalTarget">
      <div v-if="showDataModalFlag" class="modal-backdrop fade show"></div>
      <div v-if="showDataModalFlag" class="modal show d-block" tabindex="-1" role="dialog">
        <div class="modal-dialog modal-lg" role="document">
          <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ modalTitle }}
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closeDataModal"
              aria-label="Schließen"
            ></button>
          </div>
          <div class="modal-body">
            <pre class="bg-light p-3 rounded"><code>{{ formatJson(modalData) }}</code></pre>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeDataModal">
              Schließen
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { adminApi } from '@/api/admin'
import BackButton from '@/components/common/BackButton.vue'

const route = useRoute()

// Reactive data
const user = ref(null)
const history = ref([])
const loading = ref(false)
const error = ref(null)
const searchQuery = ref('')
const actionFilter = ref('')
const referenceTypeFilter = ref('')
const itemsPerPage = ref(20)
const currentPage = ref(1)
const pagination = ref({
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  itemsPerPage: 20
})

// Modal data
const showDiffModalFlag = ref(false)
const showDataModalFlag = ref(false)
const selectedEntry = ref(null)
const modalData = ref(null)
const modalTitle = ref('')

// Computed properties
const paginationInfo = computed(() => {
  const start = (pagination.value.currentPage - 1) * pagination.value.itemsPerPage + 1
  const end = Math.min(start + pagination.value.itemsPerPage - 1, pagination.value.totalItems)
  return `${start}-${end}`
})

const visiblePages = computed(() => {
  const pages = []
  const current = pagination.value.currentPage
  const total = pagination.value.totalPages
  
  let start = Math.max(1, current - 2)
  let end = Math.min(total, current + 2)
  
  if (end - start < 4) {
    if (start === 1) {
      end = Math.min(total, start + 4)
    } else {
      start = Math.max(1, end - 4)
    }
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  
  return pages
})

const hasActiveFilters = computed(() => {
  return searchQuery.value || actionFilter.value || referenceTypeFilter.value
})

// Methods
const loadHistory = async () => {
  const userId = route.params.id
  if (!userId) {
    error.value = 'Keine Benutzer-ID angegeben'
    return
  }

  loading.value = true
  error.value = null
  
  try {
    const params = {
      page: currentPage.value,
      limit: itemsPerPage.value,
      search: searchQuery.value.trim() || undefined,
      action: actionFilter.value || undefined,
      referenceType: referenceTypeFilter.value || undefined
    }
    
    const response = await adminApi.getUserHistory(userId, params)
    history.value = response.history
    pagination.value = response.pagination
    user.value = response.user
  } catch (err) {
    console.error('Error loading user history:', err)
    error.value = err.response?.data?.message || 'Fehler beim Laden der Historie'
  } finally {
    loading.value = false
  }
}

const changePage = (page) => {
  if (page >= 1 && page <= pagination.value.totalPages) {
    currentPage.value = page
    loadHistory()
  }
}

const debouncedSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    currentPage.value = 1
    loadHistory()
  }, 300)
}

const clearSearch = () => {
  searchQuery.value = ''
  currentPage.value = 1
  loadHistory()
}

const clearAllFilters = () => {
  searchQuery.value = ''
  actionFilter.value = ''
  referenceTypeFilter.value = ''
  currentPage.value = 1
  loadHistory()
}

const showDiffModal = (entry) => {
  selectedEntry.value = entry
  showDiffModalFlag.value = true
}

const closeDiffModal = () => {
  showDiffModalFlag.value = false
  selectedEntry.value = null
}

const showDataModal = (entry, dataType) => {
  selectedEntry.value = entry
  modalData.value = dataType === 'old' ? entry.oldData : entry.newData
  modalTitle.value = dataType === 'old' ? 'Gelöschte Daten' : 'Neue Daten'
  showDataModalFlag.value = true
}

const closeDataModal = () => {
  showDataModalFlag.value = false
  selectedEntry.value = null
  modalData.value = null
  modalTitle.value = ''
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

const formatJson = (data) => {
  if (!data) return 'Keine Daten'
  return JSON.stringify(data, null, 2)
}

const getActionLabel = (action) => {
  const labels = {
    'create': 'Erstellt',
    'update': 'Aktualisiert',
    'delete': 'Gelöscht'
  }
  return labels[action] || action
}

const getActionBadgeClass = (action) => {
  const classes = {
    'create': 'bg-success',
    'update': 'bg-warning',
    'delete': 'bg-danger'
  }
  return classes[action] || 'bg-secondary'
}

const getReferenceTypeLabel = (type) => {
  const labels = {
    'user': 'Benutzer',
    'receipt': 'Quittung',
    'team': 'Team',
    'billing': 'Rechnung',
    'coinConsumption': 'Coin-Verbrauch',
    'userAccount': 'Benutzerkonto',
    'email': 'E-Mail',
    'role': 'Rolle'
  }
  return labels[type] || type
}

// Timeout für debounced search
let searchTimeout = null

onMounted(() => {
  loadHistory()
})
</script>

<style scoped>
pre {
  max-height: 400px;
  overflow-y: auto;
  font-size: 0.875rem;
}

.modal-xl {
  max-width: 90%;
}

.badge {
  font-size: 0.75rem;
}
</style>
